import { saveBlobAsFile } from '../api/apiUtils.js'
import { sanitizeText } from '../constants/index.js'
import {
  BASE_URL,
  DELETE_EILE_META_DATA_API,
  DOWNLOAD_META_DATA_API,
  DOWNLOAD_SELECTED_ERROR_FILE_API,
  DOWNLOAD_SELECTED_FILE_API,
  DOWNLODE_TEMPLET_API,
  GET_FILE_DATA_TYPE_API,
  GET_FILE_SOURCE_API,
  GET_META_DATA_API,
  UPLOAD_FILE_API,
} from '../endpoints/apiEndpoints'
import { getAccessToken } from '../utils/session'
export async function getSource() {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')
  const res = await fetch(`${BASE_URL}/${GET_FILE_SOURCE_API}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) throw new Error(`Failed to get file source: ${res.status}`)
  return await res.json()
}

export async function getFileDataType() {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')
  const res = await fetch(`${BASE_URL}/${GET_FILE_DATA_TYPE_API}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) throw new Error(`Failed to get file source: ${res.status}`)
  return await res.json()
}

export async function downloadTemplate(templateName) {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  // ✅ Sanitize before use
  const safeTemplateName = sanitizeText(templateName)

  const response = await fetch(`${BASE_URL}/${DOWNLODE_TEMPLET_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ templateName: safeTemplateName }),
  })

  if (!response.ok) {
    let errorMessage = `Failed to download template: ${response.status}`
    try {
      const errData = await response.json()
      errorMessage = sanitizeText(errData?.message) || errorMessage
    } catch {
      /* ignore non-JSON error */
    }
    throw new Error(errorMessage)
  }

  // ✅ Secure Veracode-approved download
  const blob = await response.blob()
  saveBlobAsFile(blob, `${safeTemplateName}_template.xlsx`)

  return true
}

export async function uploadFile({
  file,
  source,
  dataType,
  loadType = 'Web Based',
  transformId = null,
}) {
  if (!file) throw new Error('No file provided')

  if (dataType === 'Section Keyword' && source === 'Generic') {
    throw new Error(
      'Generic source is not allowed for Section Keyword. Please select Reload.'
    )
  }

  // Determine category based on dataType
  let category = null
  if (dataType === 'Vendor Transactional Data') category = 'Returns'
  else if (['Form 140 (Original)', 'Form 144 (Original)'].includes(dataType))
    category = 'Returns'
  else if (
    [
      'Vendor Master',
      'Vendor LDC Master',
      'Vendor LDC Pdf',
      'TDS Challan Details',
      'TDS Challan Pdf',
      'Section Keywords',
      'Rule Setting',
    ].includes(dataType)
  )
    category = 'Masters'

  if (source === 'Reload') {
    transformId = -1
  }

  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const formData = new FormData()

  // If source starts with "Alt", support multiple file uploads
  const isAltSource = source?.toLowerCase().startsWith('alt')
  if (isAltSource && Array.isArray(file)) {
    file.forEach(f => {
      formData.append('file', f)
    })
  } else {
    formData.append('file', Array.isArray(file) ? file[0] : file)
  }

  formData.append('source', source)
  formData.append('category', category)
  formData.append('transformId', transformId)
  formData.append('dataType', dataType)
  formData.append('loadType', loadType)

  const res = await fetch(`${BASE_URL}/${UPLOAD_FILE_API}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  })

  if (!res.ok) {
    let errorMessage = `Failed to upload file: ${res.status}`
    try {
      const errData = await res.json()
      errorMessage = errData?.message || errorMessage
    } catch (error) {
      console.log(error, 'error')
    }
    throw new Error(errorMessage)
  }

  return await res.json()
}

export async function getMetalist(payload) {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const res = await fetch(`${BASE_URL}/${GET_META_DATA_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) throw new Error(`Failed to fetch meta data: ${res.status}`)
  return await res.json()
}

export async function downloadMetaListExcel(payload) {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const res = await fetch(`${BASE_URL}/${DOWNLOAD_META_DATA_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok)
    throw new Error(`Failed to download meta data file Excel: ${res.status}`)

  // Return as Blob (Excel file)
  const blob = await res.blob()
  return blob
}

export async function deleteUploadEntries(entries) {
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    throw new Error('Entries array is required and cannot be empty')
  }

  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const res = await fetch(`${BASE_URL}/${DELETE_EILE_META_DATA_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
    body: JSON.stringify(entries),
  })

  const jsonData = await res.json()

  if (!jsonData.success) {
    const errorMessage =
      jsonData?.errors?.[0]?.message || 'Something went wrong'

    throw new Error(errorMessage)
  }

  return
}

export const downloadSelctedFile = async payload => {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const res = await fetch(`${BASE_URL}/${DOWNLOAD_SELECTED_FILE_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok)
    throw new Error(`Failed to download meta data file Excel: ${res.status}`)

  // Return as Blob (Excel file)
  const blob = await res.blob()
  return blob
}

export const downloadSelctedErrorFile = async payload => {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Missing access token in sessionStorage')

  const res = await fetch(`${BASE_URL}/${DOWNLOAD_SELECTED_ERROR_FILE_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok)
    throw new Error(`Failed to download meta data file Excel: ${res.status}`)

  // Return as Blob (Excel file)
  const json = await res.json()
  if (!json.success) {
    throw new Error(
      `${json?.errors[0]?.message || 'Failed to download erorr file'}`
    )
  }

  return json
}
